/**
 * Typed event taxonomy for the editor onboarding mentor.
 *
 * V1 has no network sink. Events go to:
 *   - an in-memory ring buffer (see `buffer.ts`)
 *   - `console.debug` when `VITE_ONBOARDING_DEBUG === "1"`
 *
 * The `emit()` signature is stable so Phase 1.x can add a network sink
 * (POST to `VITE_ONBOARDING_EVENT_SINK_URL`) without touching call sites.
 */

import type { EditorStepId, SessionStatus } from "../persistence/schema";
import { pushEvent } from "./buffer";

export type OnboardingEvent =
  | {
      type: "mentor.started";
      sessionId: string;
      userId: string;
      branch: "blank" | "template";
      templateId: string | null;
      ts: string;
    }
  | {
      type: "mentor.resumed";
      sessionId: string;
      from: SessionStatus;
      ts: string;
    }
  | {
      type: "mentor.paused";
      sessionId: string;
      atStep: EditorStepId;
      ts: string;
    }
  | {
      type: "mentor.skipped";
      sessionId: string;
      atStep: EditorStepId;
      ts: string;
    }
  | {
      type: "mentor.dismissed";
      sessionId: string;
      atStep: EditorStepId;
      ts: string;
    }
  | {
      type: "mentor.restarted";
      sessionId: string;
      from: "profile" | "editor_top_bar";
      ts: string;
    }
  | {
      type: "mentor.completed";
      sessionId: string;
      outcome: "published" | "finished_without_publish";
      durationMs: number;
      ts: string;
    }
  | {
      type: "step.entered";
      sessionId: string;
      stepId: EditorStepId;
      ts: string;
    }
  | {
      type: "step.bypassed";
      sessionId: string;
      stepId: EditorStepId;
      reason: "auto_pass" | "user_skip" | "target_not_found";
      ts: string;
    }
  | {
      type: "step.completed";
      sessionId: string;
      stepId: EditorStepId;
      durationMs: number;
      ts: string;
    }
  | {
      type: "step.softened";
      sessionId: string;
      stepId: EditorStepId;
      reason: "no_activity_60s" | "wrong_block_added";
      ts: string;
    }
  | {
      type: "step.failed";
      sessionId: string;
      stepId: EditorStepId;
      error: string;
      ts: string;
    }
  | {
      type: "target.not_found";
      sessionId: string;
      stepId: EditorStepId;
      targetId: string;
      ts: string;
    }
  | { type: "preview.opened"; sessionId: string; ts: string }
  | { type: "preview.popup_blocked"; sessionId: string; ts: string }
  | { type: "publish.attempted"; sessionId: string; ts: string }
  | {
      type: "publish.failed";
      sessionId: string;
      error: string;
      ts: string;
    }
  | {
      type: "publish.succeeded";
      sessionId: string;
      durationMs: number;
      ts: string;
    }
  | { type: "rbac.no_publish_branch"; sessionId: string; ts: string };

export type OnboardingEventType = OnboardingEvent["type"];

function debugLogEnabled(): boolean {
  try {
    return (
      typeof import.meta !== "undefined" &&
      (import.meta as { env?: Record<string, string | undefined> }).env
        ?.VITE_ONBOARDING_DEBUG === "1"
    );
  } catch {
    return false;
  }
}

export function emit(event: OnboardingEvent): void {
  pushEvent(event);
  if (debugLogEnabled()) {
    // eslint-disable-next-line no-console
    console.debug("[onboarding]", event);
  }
}

export function nowIso(): string {
  return new Date().toISOString();
}
